import { List, Map, OrderedMap, fromJS, is } from 'immutable';
import moment from 'moment';

import Actions from 'flow/actions';
import { listToMap } from 'flow/utils';
import { fieldsCoveredByIncludes } from 'flow/utils/call-record-fetch-includes';

// -------------------------------------------------------------------------
// State shape (see flow/types/CallRecord.ts: CallRecordsState):
//
//   state.callRecordsData = Map({
//       callRecordsUniversal: Map<CallRecordKey, CallRecord>, // canonical entities (cross-scope)
//       sortingData: Map({
//           extensions: Map<ExtensionKey, ScopeBucket>,
//           queues:     Map<QueueKey,     ScopeBucket>,     // populated by other reducers later
//       }),
//   })
//
//   ScopeBucket = Map({ all: ScopeSlice, byPhoneNumber: Map<string, ScopeSlice> })
//   ScopeSlice  = Map({
//       entries: OrderedMap<CallRecordKey, SortingEntry>,   // ordered by connected DESC
//       nextBefore: number | null,
//       nextSince:  number | null,
//   })
//   SortingEntry = Map({ key, connected, disconnected, answered })
//
// Solo-fetched records (deep links) write to `callRecordsUniversal` only; they
// don't belong to a list, so they get no sorting entry.
// -------------------------------------------------------------------------

const initialState = Map({
    callRecordsUniversal: Map(),
    sortingData: Map({
        extensions: Map(),
        queues: Map(),
    }),
});

const emptySlice = Map({
    entries: OrderedMap(),
    nextBefore: null,
    nextSince: null,
});

const emptyBucket = Map({
    all: emptySlice,
    byPhoneNumber: Map(),
});

const extractSortingEntry = (record) =>
    Map({
        key: record.get('key'),
        connected: record.get('connected'),
        disconnected: record.get('disconnected'),
        answered: record.get('answered'),
        direction: record.get('direction'),
        ohwOrdered: record.get('ohwOrdered'),
    });

const sortEntriesByConnectedDesc = (entries) =>
    entries.sort((a, b) => {
        const aConnected = a.get('connected');
        const bConnected = b.get('connected');
        if (!aConnected || !bConnected) return 0;
        return moment(bConnected).diff(moment(aConnected));
    });

export const updateBeforeAndSince = (slice, incomingData) => {
    if (!slice || !incomingData) return slice;

    // The response echoes the `since` and `before` it was asked for, so we can
    // tell which cursor it actually carries news about.
    const isSinceAnchoredPage = !!incomingData.get('since');

    return slice
        .update('nextBefore', (nextBefore) => {
            const incomingNextBefore = incomingData.get('nextBefore');
            if (incomingNextBefore == null) {
                // A page fetched from a `since` says nothing about what lies
                // further back, so only a page paginating backwards may report
                // that there is nothing left there. Otherwise a fetch for newer
                // records that happens to come back empty would end the history
                // list's infinite scroll.
                return isSinceAnchoredPage ? nextBefore : null;
            }
            if (!nextBefore) {
                return incomingNextBefore;
            }
            return moment(nextBefore).isBefore(moment(incomingNextBefore))
                ? nextBefore
                : incomingNextBefore;
        })
        .update('nextSince', (nextSince) => {
            if (!incomingData.get('nextSince')) {
                return nextSince;
            }
            if (!nextSince) {
                return incomingData.get('nextSince');
            }
            return moment(nextSince).isAfter(
                moment(incomingData.get('nextSince')),
            )
                ? nextSince
                : incomingData.get('nextSince');
        });
};

const ensureBucketAt = (state, slicePath) => {
    // slicePath: ['sortingData', 'extensions' | 'queues', key, ...]
    // Ensures the ScopeBucket exists at ['sortingData', kind, key].
    const [, kind, scopeKey] = slicePath;
    return state.updateIn(['sortingData', kind, scopeKey], (existing) =>
        existing ? existing : emptyBucket,
    );
};

const mergeEntriesIntoSlice = (
    state,
    slicePath,
    fetchedRecords,
    fetchedData,
) => {
    // Every fetched record gets an entry in sortingData, including ongoing
    // calls. History list selectors filter by `disconnected`; consumers that
    // need active records (e.g. the post-call-end refetch's "earliest active
    // connected" timestamp) read them straight from here.
    const newEntries = fetchedRecords
        .toOrderedMap()
        .mapEntries(([, record]) => [
            record.get('key'),
            extractSortingEntry(record),
        ]);

    return state.updateIn(slicePath, emptySlice, (slice) => {
        const entries = slice.get('entries') || OrderedMap();
        // A fetch writes the entries again, mostly equal to the ones in the
        // store. The slice keeps its entries while they are equal, so the
        // history lists do not render again, and the sort is skipped.
        const entriesAreEqual = newEntries.every((entry, key) =>
            is(entries.get(key), entry),
        );
        return slice
            .set(
                'entries',
                entriesAreEqual
                    ? entries
                    : sortEntriesByConnectedDesc(entries.merge(newEntries)),
            )
            .update((s) => updateBeforeAndSince(s, fetchedData));
    });
};

// Merge a single incoming record into the existing stored record, honoring the
// fetch's include scope. For fields the fetch was authoritative for (covered by
// the include), the incoming value wins — including its absence, which means
// the field was deleted server-side and should clear from the stored record.
// For fields outside the include scope, the existing value is preserved so a
// slim fetch doesn't wipe data populated by an earlier richer fetch.
//
// A fetch writes each record again, mostly with equal data. The record keeps
// its object while it is equal, so the rows and pages that show it do not
// render again.
const mergeRecordWithIncludeScope = (existing, incoming, includedFields) => {
    if (!existing) return incoming;
    const merged = existing.merge(incoming);
    const scoped =
        includedFields.size === 0
            ? merged
            : merged.withMutations((mutable) => {
                  existing.forEach((_value, key) => {
                      if (includedFields.has(key) && !incoming.has(key)) {
                          mutable.delete(key);
                      }
                  });
              });
    return is(existing, scoped) ? existing : scoped;
};

const mergeRecordsIntoCanonical = (state, mappedRecords, include) => {
    const includedFields = fieldsCoveredByIncludes(include);
    return state.update('callRecordsUniversal', Map(), (existing) =>
        mappedRecords.reduce(
            (acc, incoming, key) =>
                acc.set(
                    key,
                    mergeRecordWithIncludeScope(
                        acc.get(key),
                        incoming,
                        includedFields,
                    ),
                ),
            existing,
        ),
    );
};

const updateRecord = (state, callRecordKey, mutator) => {
    if (!state.hasIn(['callRecordsUniversal', callRecordKey])) return state;
    return state.updateIn(['callRecordsUniversal', callRecordKey], mutator);
};

const removeKeyFromAllIndices = (state, callRecordKey) =>
    state.update('sortingData', (sortingData) =>
        sortingData.map((kindMap) =>
            kindMap.map((bucket) =>
                bucket
                    .update('all', emptySlice, (slice) =>
                        slice.update('entries', OrderedMap(), (entries) =>
                            entries.delete(callRecordKey),
                        ),
                    )
                    .update('byPhoneNumber', Map(), (perNumber) =>
                        perNumber.map((slice) =>
                            slice.update('entries', OrderedMap(), (entries) =>
                                entries.delete(callRecordKey),
                            ),
                        ),
                    ),
            ),
        ),
    );

const removeActionByKey = (list = List(), actionKey) => {
    const index = list.findIndex(
        (callAction) => callAction.get('key') === actionKey,
    );
    return index === -1 ? list : list.delete(index);
};

// Apply a fetch response to the canonical entity store and the matching
// scope slice. `scope` is { type: 'extension' | 'queue', key, phoneNumber? }.
// Phone-number-less scopes target the bucket's `all` slice; scopes with a
// `phoneNumber` target the matching `byPhoneNumber` sub-slice.
const applyFetchedRecords = (state, scope, data, include) => {
    if (!scope || !scope.key) return state;

    const fetchedData = fromJS(data);
    const fetchedRecords = fetchedData.get('records', List());
    const mapped = listToMap(fetchedRecords, (item) => item.get('key'));

    const kind = scope.type === 'queue' ? 'queues' : 'extensions';
    const bucketPath = ['sortingData', kind, scope.key];
    const slicePath = scope.phoneNumber
        ? [...bucketPath, 'byPhoneNumber', scope.phoneNumber]
        : [...bucketPath, 'all'];

    return mergeEntriesIntoSlice(
        ensureBucketAt(
            mergeRecordsIntoCanonical(state, mapped, include),
            bucketPath,
        ),
        slicePath,
        fetchedRecords,
        fetchedData,
    );
};

// eslint-disable-next-line complexity
export const callRecordsReducer = (state = initialState, action) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE: {
            return initialState;
        }
        case Actions.RESULT_CALL_RECORDS_FETCHED:
            return applyFetchedRecords(
                state,
                action.scope,
                action.data,
                action.include,
            );
        case Actions.RESULT_CALL_RECORD_FOR_SCOPE: {
            // One record, not a page: it says nothing about what else exists, so
            // the pagination cursors are left alone. Fetching by key is how a call
            // that has just ended is picked up, since the list endpoint can only be
            // asked for calls that *started* in a window -- which for a long call
            // would mean asking for everything back to when it started.
            const record = fromJS(action.data);
            const kind =
                action.scope.type === 'queue' ? 'queues' : 'extensions';
            const bucketPath = ['sortingData', kind, action.scope.key];

            return mergeEntriesIntoSlice(
                ensureBucketAt(
                    mergeRecordsIntoCanonical(
                        state,
                        Map({ [action.callRecordKey]: record }),
                        action.include,
                    ),
                    bucketPath,
                ),
                [...bucketPath, 'all'],
                List([record]),
            );
        }
        case Actions.CLEAR_QUEUE_CALL_RECORDS:
            return state.deleteIn(['sortingData', 'queues', action.key]);
        case Actions.CLEAR_QUEUE_CALL_RECORDS_BPART_SPECIFIC:
            return state.deleteIn([
                'sortingData',
                'queues',
                action.key,
                'byPhoneNumber',
                action.bPartNumber,
            ]);
        case Actions.EXTRACT_CALL_RECORDS_FOR_NUMBER: {
            // Seed an extension's byPhoneNumber sub-slice from records already
            // present in the canonical entity store (no extra network fetch).
            const { extensionKey, number } = action;
            if (!extensionKey || !number) return state;

            const matchingRecords = state
                .get('callRecordsUniversal', Map())
                .filter(
                    (record) => record.getIn(['number', 'number']) === number,
                );

            const slicePath = [
                'sortingData',
                'extensions',
                extensionKey,
                'byPhoneNumber',
                number,
            ];

            return mergeEntriesIntoSlice(
                ensureBucketAt(state, [
                    'sortingData',
                    'extensions',
                    extensionKey,
                ]),
                slicePath,
                matchingRecords.toList(),
                // Carry over the bucket's `all` cursors so we don't lose
                // pagination context when seeding the sub-slice.
                state.getIn(
                    ['sortingData', 'extensions', extensionKey, 'all'],
                    emptySlice,
                ),
            );
        }
        case Actions.RESULT_CALL_RECORD: {
            // Solo fetch — write the entity only; not part of any list.
            // Include-aware merge so a solo fetch with a narrower include
            // (e.g. the CTI flow) doesn't drop fields populated by an earlier
            // richer fetch.
            const record = fromJS(action.data);
            const includedFields = fieldsCoveredByIncludes(action.include);
            return state.update('callRecordsUniversal', Map(), (existing) =>
                existing.set(
                    action.callRecordKey,
                    mergeRecordWithIncludeScope(
                        existing.get(action.callRecordKey),
                        record,
                        includedFields,
                    ),
                ),
            );
        }
        case Actions.RESULT_QUEUE_CALL_RECORDS_UNHANDLED_WIDGET: {
            // The missed queue calls widget keeps which calls it shows; the
            // rows read each call from here, as the call history's rows do.
            // The calls belong to no list here, so they get no sorting entry.
            const fetchedRecords = fromJS(action.data).get('records', List());
            return mergeRecordsIntoCanonical(
                state,
                listToMap(fetchedRecords, (item) => item.get('key')),
                action.include,
            );
        }
        case Actions.SET_OMNI_TICKET_KEY_QUEUE_CALL: {
            return updateRecord(state, action.callKey, (record) =>
                record.set('omniTicketEntityKey', action.ticketKey),
            );
        }
        case Actions.SET_VOICEMESSAGE_READ: {
            if (action.callRecordKey) {
                return updateRecord(state, action.callRecordKey, (record) =>
                    record.hasIn(['voiceMessage', 'voiceMessageState'])
                        ? record.setIn(
                              ['voiceMessage', 'voiceMessageState'],
                              'read',
                          )
                        : record,
                );
            }

            // No callRecordKey — locate the record by voiceMessage.uniqueId.
            const records = state.get('callRecordsUniversal', Map());
            const matchingKey = records.findKey(
                (record) =>
                    record.getIn(['voiceMessage', 'uniqueId']) ===
                    action.voiceMessageUniqueId,
            );
            if (!matchingKey) return state;

            return state.setIn(
                [
                    'callRecordsUniversal',
                    matchingKey,
                    'voiceMessage',
                    'voiceMessageState',
                ],
                'read',
            );
        }
        case Actions.UPDATE_VOICEMESSAGES_AFTER_DELETE: {
            if (action.callRecordKey) {
                return state.deleteIn([
                    'callRecordsUniversal',
                    action.callRecordKey,
                    'voiceMessage',
                ]);
            }

            // No callRecordKey — locate and remove the record(s) by uniqueId.
            const records = state.get('callRecordsUniversal', Map());
            const keysToRemove = records
                .filter(
                    (record) =>
                        record.has('voiceMessage') &&
                        record.getIn(['voiceMessage', 'uniqueId']) ===
                            action.voiceMessageUniqueId,
                )
                .keySeq()
                .toArray();

            return keysToRemove.reduce(
                (acc, key) =>
                    removeKeyFromAllIndices(
                        acc.deleteIn(['callRecordsUniversal', key]),
                        key,
                    ),
                state,
            );
        }
        case Actions.RESULT_DELETE_PERSONAL_RECORDED_CALL: {
            return updateRecord(state, action.callRecordKey, (callRecord) =>
                callRecord.delete('recording').delete('recordedCall'),
            );
        }
        case Actions.RESULT_DELETE_TRANSCRIPTION: {
            return updateRecord(state, action.callRecordKey, (callRecord) =>
                callRecord.delete('extendedTranscriptionInfo'),
            );
        }
        case Actions.RESULT_VOICEMAILS_DELETE_ALL: {
            return state.update('callRecordsUniversal', Map(), (records) =>
                records.map((record) => record.delete('voiceMessage')),
            );
        }
        case Actions.RESULT_NEW_EXTERNAL_CONTACT: {
            const contact = fromJS(action.data);
            const fixed = contact.getIn(['fixed', 'number']);
            const mobile = contact.getIn(['mobile', 'number']);
            const technical = contact.getIn(['technicalNumber', 'number']);

            return state.update('callRecordsUniversal', Map(), (records) =>
                records.map((record) => {
                    const number = record.getIn(['number', 'number']);
                    const matches =
                        number &&
                        (number === fixed ||
                            number === mobile ||
                            number === technical);
                    return matches ? record.set('contact', contact) : record;
                }),
            );
        }
        case Actions.REMOVE_EXTERNAL_CONTACT: {
            const contactKey = action.key;
            return state.update('callRecordsUniversal', Map(), (records) =>
                records.map((record) =>
                    record.getIn(['contact', 'key']) === contactKey
                        ? record.delete('contact')
                        : record,
                ),
            );
        }
        case Actions.RESULT_UPDATED_CONTACT: {
            const contact = fromJS(action.data);
            const contactKey = contact.get('key');
            if (!contactKey) return state;

            return state.update('callRecordsUniversal', Map(), (records) =>
                records.map((record) =>
                    record.getIn(['contact', 'key']) === contactKey
                        ? record.set('contact', contact)
                        : record,
                ),
            );
        }
        case Actions.RESULT_CREATE_CALL_ACTION:
        case Actions.RESULT_CREATE_CALL_ACTION_PERSONAL_CALL: {
            const callActionDTO = fromJS(action.data);
            return updateRecord(state, action.callRecordKey, (record) =>
                record.update('actions', List(), (prev) =>
                    prev.push(callActionDTO),
                ),
            );
        }
        case Actions.RESULT_DELETE_CALL_ACTION:
        case Actions.RESULT_DELETE_CALL_ACTION_PERSONAL_CALL: {
            return updateRecord(state, action.callRecordKey, (record) =>
                record.update('actions', List(), (prev) =>
                    removeActionByKey(prev, action.callActionKey),
                ),
            );
        }
        case Actions.SET_EXTENDED_TRANSCRIPTION_INFO: {
            return updateRecord(state, action.callRecordKey, (record) =>
                record.set(
                    'extendedTranscriptionInfo',
                    fromJS(action.extendedTranscriptionInfo),
                ),
            );
        }
        default: {
            return state;
        }
    }
};

export default callRecordsReducer;
